<template>
  <div class="product-images-container">
    <ValidationProvider
      v-slot="{ errors }"
      name="Main Image"
      rules="required"
      vid="main-image"
    >
      <div>
        <div class="d-flex justify-content-between">
          <p class="section-title">
            Main Image
            <span class="required"> *</span>
          </p>
          <button
            class="browse-images-btn"
            @click="handleBrowseImage('main')"
          >
            <add-photo-icon color="#fff" />
            <span class="text">Browse Image</span>
          </button>
        </div>
        <img
          class="main-image"
          :src="productImageUrls[0]"
          alt="Product"
          @error="(event) => event.target.src = getDefaultProductImage(100, 100)"
        >
        <div class="error-section">
          <small class="text-danger">{{ errors[0] }}</small>
        </div>
      </div>
    </ValidationProvider>
    <div class="d-flex justify-content-between mt-4">
      <p class="section-title">
        Other Images
      </p>
    </div>
    <div
      v-if="productImageUrls.length > 1"
      class="d-flex mt-1 flex-wrap images-row-gap"
    >
      <div
        v-for="(image, imageIndex) in productImageUrls.slice(1)"
        :key="imageIndex"
        class="images-row-item bg-white"
      >
        <div class="index">
          {{ imageIndex + 2 }}
        </div>
        <div
          class="image-delete-icon"
          @click="removeImage(image)"
        >
          <x-icon size="0.9x" />
        </div>
        <img
          class="other-image"
          :src="image"
          alt="Product"
          @error="(event) => event.target.src = getDefaultProductImage(100, 100)"
        >
      </div>
    </div>
    <drag-and-drop
      @drop="handleDrop"
      @browse-image="handleBrowseImage('other')"
    />
    <input
      ref="fileInputMain"
      type="file"
      accept="image/*"
      hidden
      :maxlength="1"
      @change="handleFileChange"
    >
    <input
      ref="fileInputOther"
      type="file"
      accept="image/*"
      hidden
      multiple
      :maxlength="maxSelectionLength"
      @change="handleFileChange"
    >
  </div>
</template>
<script>
import AddPhotoIcon from '@/@core/assets/svg-components/AddPhotoIcon.vue'
import store from '@/store'
import { XIcon } from 'vue-feather-icons'
import { ValidationProvider } from 'vee-validate'
import { SET_PRODUCT_IMAGES } from '@/store/modules/productEdit.module'
import { getDefaultProductImage } from '@/@core/utils/utils'
import { apiToastError } from '@/@core/utils/toast'
import constants, { MAX_FILE_SIZE, PRODUCT_EDIT_VALID_IMAGE_EXTENSIONS } from '@/constants'
import analytics from '@/@core/utils/analytics'
import DragAndDrop from './DragAndDrop.vue'

export default {
  name: 'ImagesSection',
  components: {
    AddPhotoIcon,
    XIcon,
    DragAndDrop,
    ValidationProvider,
  },
  data() {
    return {
      type: null,
      getDefaultProductImage,
    }
  },
  computed: {
    productImageUrls() {
      const images = store.state.productEdit.productImages || []
      return images.sort((a, b) => a.index - b.index).map(image => image.url)
    },
    maxSelectionLength() {
      return this.type === 'main' ? 1 : 9
    },
  },
  beforeDestroy() {
    this.cleanupLocalUrls()
  },
  methods: {
    handleBrowseImage(type) {
      if (this.productImageUrls.length === 10 && type === 'other') {
        apiToastError('You can upload maximum 10 images.')
        return
      }
      this.type = type
      this.$refs[`fileInput${type === 'main' ? 'Main' : 'Other'}`].click()
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_BROWSE_IMAGE_BUTTON, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        imageType: type,
      })
    },
    handleFiles(files) {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_SELECTS_IMAGE, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        imageType: this.type,
        imageNames: files.length ? Array.from(files).map(file => file.name) : [],
      })
      const localFiles = []

      const validFiles = Array.from(files).filter(file => PRODUCT_EDIT_VALID_IMAGE_EXTENSIONS.includes(file.type.split('/')[1]))

      if (validFiles.length !== files.length) {
        apiToastError('Invalid image format. Only PNG, JPG, JPEG, GIF, and WebP are allowed.')
      }

      if (validFiles.length === 0) return []

      validFiles.forEach((file, index) => {
        if (file.size > MAX_FILE_SIZE) {
          apiToastError(`"${file.name}" exceeds the 20MB size limit.`)
        } else {
          const localUrl = URL.createObjectURL(file)
          const localImage = {
            file,
            url: localUrl,
            index: this.type === 'main' ? 0 : (this.productImageUrls?.length + index + 1 || 0),
            isLocal: true,
          }
          localFiles.push(localImage)
        }
      })
      return localFiles
    },
    storeNewImages(localFiles) {
      let newImages = [...store.state.productEdit.productImages]
      if (this.type === 'main') {
        if (newImages.some(img => img.index === 0)) {
          newImages = newImages.map(img => (img.index === 0 ? localFiles[0] : img))
        } else {
          newImages.unshift(localFiles[0])
        }
      } else {
        newImages.push(...localFiles)
      }
      if (newImages.length > 10) {
        newImages = newImages.slice(0, 10)
        apiToastError('You can upload maximum 10 images.')
      }
      store.commit(SET_PRODUCT_IMAGES, newImages)
    },
    async handleFileChange(event) {
      const files = event.target.files
      if (!files || !files.length) return
      const localFiles = this.handleFiles(files)

      if (localFiles.length !== 0) {
        this.storeNewImages(localFiles)
      }
      event.target.value = null
    },
    handleDrop(files) {
      this.type = 'other'
      if (this.productImageUrls.length === 10 && this.type === 'other') {
        apiToastError('You can upload maximum 10 images.')
        return
      }
      if (!files || !files.length) return

      const localFiles = this.handleFiles(files)

      this.storeNewImages(localFiles)
    },
    cleanupLocalUrls() {
      const localImages = store.state.productEdit.productImages
      const localImageUrls = localImages.filter(img => img.isLocal).map(img => img.url)
      localImageUrls.forEach(url => {
        URL.revokeObjectURL(url)
      })

      store.commit(SET_PRODUCT_IMAGES, localImages.filter(img => !img.isLocal))
    },
    removeImage(imageToDelete) {
      const image = store.state.productEdit.productImages.find(img => img.url === imageToDelete)
      if (!image) return

      const updatedImages = [...store.state.productEdit.productImages]

      if (image.isLocal) {
        URL.revokeObjectURL(image.url)
      }
      updatedImages.splice(updatedImages.indexOf(image), 1)
      const updatedImagesWithNewIndex = updatedImages.map((img, index) => ({
        ...img,
        index,
      }))
      store.commit(SET_PRODUCT_IMAGES, updatedImagesWithNewIndex)
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_REMOVE_IMAGE_ICON, {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        imageType: 'other',
        imageUrl: imageToDelete,
      })
    },
  },
}
</script>
<style lang="scss">
.product-images-container {
  padding: 16px;
  padding-bottom: 22px;
  border: 1px solid #D8D6DD;
  width: 636px;
  min-height: 677px;
  border-radius: 8px;
}
.section-title {
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  margin-bottom: 10px;

  .required {
    font-weight: 400;
  }
}

.browse-images-btn {
  border-radius: 4px;
  border: none !important;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 6px;
  background-color: #90A0B7 !important;
  color: #fff !important;

  svg {
    position: relative;
    top: -2px;
  }

  .text {
    color: #fff;
    font-size: 8px;
    line-height: 12px;
    font-weight: 500;
  }
}

.main-image {
  display: block;
  margin: auto;
  margin-top: 40px;
  max-height: 165px;
}

.images-row-gap {
  gap: 10px;
}

.images-row-item {
  width: 75px;
  height: 75px;
  border: 1px solid #D8D6DD;
  border-radius: 5px;
  background-color: #F6F6F6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;

  &:hover {
    border: 1px solid #292929;
    .image-delete-icon {
      display: flex;
    }
  }

  .index {
    position: absolute;
    top: 3px;
    left: 3px;
    font-size: 10px;
    line-height: 12px;
    background-color: #90A0B7;
    color: #fff;
    border-radius: 3px;
    font-size: 8px;
    line-height: 12px;
    width: 15px;
    height: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .image-delete-icon {
    position: absolute;
    top: -6px;
    right: -6px;
    cursor: pointer;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: #FF4C4F;
    color: #fff;
    display: none;
    align-items: center;
    justify-content: center;
  }
  .text {
    font-size: 10px;
    line-height: 12px;
    color: #90A0B7;
    margin-top: 4px;
  }

  .other-image {
    margin: 0 auto;
    max-width: 100%;
    max-height: 100%;
    object-fit: cover;
    aspect-ratio: inherit;
    border-radius: 5px;
  }
}

.bg-white {
  background-color: #fff;
}
</style>
